import { classnames } from '@aibees/shared/utils';
import { Tooltip } from '@mui/material';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { useIsTextTrimmed } from './hooks/use-is-text-trimmed';
import * as Styled from './styles';
import type { TextEllipsisProps as Props } from './types';

export const TextEllipsis = forwardRef<HTMLSpanElement, Props>(
  (
    {
      className,
      w,
      value,
      children,
      capitalize,
      tooltipValue,
      showTooltip = true,
      popperProps,
      tooltipProps,
      variant,
      to,
      ...props
    },
    ref
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const onClick = () => {
      if (to) {
        window.open(to, '_blank');
        return;
      }
      return;
    };

    // Memoize the value to prevent recalculation on every render
    const val = useMemo(() => value ?? children, [value, children]);

    // Use boolean state more efficiently - only toggle when needed
    const [mouseEnter, setMouseEnter] = useState<boolean>(false);

    const isTextTrimmed = useIsTextTrimmed(wrapperRef, val, w, mouseEnter);

    // Memoize the tooltip title to prevent recalculation on every render
    const tooltipTitle = useMemo(
      () => (showTooltip && isTextTrimmed ? tooltipValue || val : undefined),
      [showTooltip, isTextTrimmed, tooltipValue, val]
    );

    // Memoize the className to prevent recalculation on every render
    const wrapperClassName = useMemo(
      () => classnames('TextEllipsis', className, { capitalize }),
      [className, capitalize]
    );

    // Memoize the mouse enter handler to prevent recreation on every render
    const handleMouseEnter = useCallback(() => {
      setMouseEnter(true);
    }, []);

    // Memoize the mouse leave handler to prevent recreation on every render
    const handleMouseLeave = useCallback(() => {
      setMouseEnter(false);
    }, []);

    // Memoize the ref callback to handle both internal and external refs
    const refCallback = useCallback(
      (node: HTMLDivElement) => {
        wrapperRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref && 'current' in ref) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (ref as any).current = node;
        }
      },
      [ref]
    );

    // Memoize the wrapper props to prevent object recreation
    const wrapperProps = useMemo(
      () => ({
        ref: refCallback,
        variant: variant ?? 'body2',
        ...props,
        className: wrapperClassName,
        maxWidth: w,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      }),
      [
        refCallback,
        variant,
        props,
        wrapperClassName,
        w,
        handleMouseEnter,
        handleMouseLeave,
      ]
    );

    // Memoize the tooltip props to prevent object recreation
    const tooltipComponentProps = useMemo(
      () => ({
        title: tooltipTitle,
        enterDelay: 500,
        PopperProps: popperProps,
        ...tooltipProps,
      }),
      [tooltipTitle, popperProps, tooltipProps]
    );

    return (
      <Tooltip {...tooltipComponentProps}>
        <Styled.Wrapper
          onClick={onClick}
          {...wrapperProps}
          className={classnames({ hasLink: to })}
        >
          {val}
        </Styled.Wrapper>
      </Tooltip>
    );
  }
);

TextEllipsis.displayName = 'TextEllipsis';
